import { useState } from 'react';
import { type Control, useFieldArray, useWatch } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, FieldLabel } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { FieldType, ScreenLayout } from '@/db/schema';

const FIELD_TYPES: FieldType[] = [
  'string',
  'password',
  'number',
  'currency',
  'boolean',
  'date',
  'datetime',
  'enum',
  'relation',
  'multi-relation',
  'component',
  'file',
];

const FILTER_TYPES = ['text', 'number', 'select', 'date', 'period'] as const;

function TypeSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

/**
 * Editor simples (sem drag-and-drop) das 3 listas que compõem uma screen.
 * Cada linha é só um conjunto de inputs — quem quer reordenar edita a ordem manualmente.
 */
export function LayoutEditor({ control }: { control: Control<any> }) {
  const columns = useFieldArray({ control, name: 'layout.columns' });
  const filters = useFieldArray({ control, name: 'layout.filters' });
  const fields = useFieldArray({ control, name: 'layout.fields' });

  return (
    <div className="space-y-6 col-span-12">
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Colunas (listagem)</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              columns.append({ name: '', label: '', type: 'string', sortable: true })
            }
          >
            Adicionar coluna
          </Button>
        </div>

        {columns.fields.map((row, index) => (
          <div key={row.id} className="grid grid-cols-12 gap-2 items-end border-b pb-3">
            <Field className="col-span-3">
              <FieldLabel>Campo</FieldLabel>
              <Input
                {...control.register(`layout.columns.${index}.name`)}
                placeholder="nome"
              />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Label</FieldLabel>
              <Input
                {...control.register(`layout.columns.${index}.label`)}
                placeholder="Nome"
              />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Tipo</FieldLabel>
              <ColumnTypeControl control={control} index={index} />
            </Field>
            <div className="col-span-2 flex items-center gap-2 h-9">
              <SortableControl control={control} index={index} />
              <span className="text-sm">Ordenável</span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className="col-span-1"
              onClick={() => columns.remove(index)}
            >
              Remover
            </Button>

            <RelationInputs control={control} listName="columns" index={index} />
            <MaskListInput control={control} name={`layout.columns.${index}.mask`} />
          </div>
        ))}
      </Card>

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Filtros</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => filters.append({ name: '', label: '', type: 'text' })}
          >
            Adicionar filtro
          </Button>
        </div>

        {filters.fields.map((row, index) => (
          <div key={row.id} className="grid grid-cols-12 gap-2 items-end border-b pb-3">
            <Field className="col-span-4">
              <FieldLabel>Campo</FieldLabel>
              <Input
                {...control.register(`layout.filters.${index}.name`)}
                placeholder="nome"
              />
            </Field>
            <Field className="col-span-4">
              <FieldLabel>Label</FieldLabel>
              <Input
                {...control.register(`layout.filters.${index}.label`)}
                placeholder="Nome"
              />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Tipo</FieldLabel>
              <FilterTypeControl control={control} index={index} />
            </Field>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className="col-span-1"
              onClick={() => filters.remove(index)}
            >
              Remover
            </Button>

            <RelationInputs control={control} listName="filters" index={index} />
          </div>
        ))}
      </Card>

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Campos (formulário)</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              fields.append({
                name: '',
                label: '',
                type: 'string',
                required: false,
                showOnCreate: true,
                showOnEdit: true,
                showOnView: true,
              })
            }
          >
            Adicionar campo
          </Button>
        </div>

        {fields.fields.map((row, index) => (
          <div key={row.id} className="grid grid-cols-12 gap-2 items-end border-b pb-3">
            <Field className="col-span-3">
              <FieldLabel>Campo</FieldLabel>
              <Input
                {...control.register(`layout.fields.${index}.name`)}
                placeholder="nome"
              />
            </Field>
            <Field className="col-span-3">
              <FieldLabel>Label</FieldLabel>
              <Input
                {...control.register(`layout.fields.${index}.label`)}
                placeholder="Nome"
              />
            </Field>
            <Field className="col-span-2">
              <FieldLabel>Tipo</FieldLabel>
              <FieldTypeControl control={control} index={index} />
            </Field>
            <div className="col-span-2 flex items-center gap-2 h-9">
              <RequiredControl control={control} index={index} />
              <span className="text-sm">Obrigatório</span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className="col-span-2"
              onClick={() => fields.remove(index)}
            >
              Remover
            </Button>

            <RelationInputs control={control} listName="fields" index={index} />
            <MaskListInput control={control} name={`layout.fields.${index}.mask`} />
          </div>
        ))}
      </Card>
    </div>
  );
}

// Selects/switches controlados via Controller inline pra não precisar importar em cada linha.
import { Controller } from 'react-hook-form';

function ColumnTypeControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.columns.${index}.type`}
      render={({ field }) => (
        <TypeSelect value={field.value} onChange={field.onChange} options={FIELD_TYPES} />
      )}
    />
  );
}

function FilterTypeControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.filters.${index}.type`}
      render={({ field }) => (
        <TypeSelect
          value={field.value}
          onChange={field.onChange}
          options={FILTER_TYPES}
        />
      )}
    />
  );
}

function FieldTypeControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.fields.${index}.type`}
      render={({ field }) => (
        <TypeSelect value={field.value} onChange={field.onChange} options={FIELD_TYPES} />
      )}
    />
  );
}

function SortableControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.columns.${index}.sortable`}
      render={({ field }) => (
        <Switch checked={!!field.value} onCheckedChange={field.onChange} />
      )}
    />
  );
}

function RequiredControl({ control, index }: { control: Control<any>; index: number }) {
  return (
    <Controller
      control={control}
      name={`layout.fields.${index}.required`}
      render={({ field }) => (
        <Switch checked={!!field.value} onCheckedChange={field.onChange} />
      )}
    />
  );
}

/**
 * Máscaras (ver src/lib/mask.ts) — digita uma e aperta Enter pra adicionar, podendo ter
 * várias (ex: telefone fixo vs celular). No front elas formatam a exibição/digitação; o
 * backend sempre recebe/grava o valor sem máscara.
 */
function MaskListInput({ control, name }: { control: Control<any>; name: string }) {
  const [draft, setDraft] = useState('');

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const masks: string[] = field.value ?? [];

        const addMask = () => {
          const value = draft.trim();
          if (!value || masks.includes(value)) return;
          field.onChange([...masks, value]);
          setDraft('');
        };

        const removeMask = (mask: string) => {
          field.onChange(masks.filter((m) => m !== mask));
        };

        return (
          <Field className="col-span-12">
            <FieldLabel>Máscaras (opcional — ex: (99) 9999-9999)</FieldLabel>
            <div className="flex items-center gap-2">
              <Input
                value={draft}
                placeholder="Digite a máscara e aperte Enter"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addMask();
                  }
                }}
              />
              <Button type="button" size="sm" variant="outline" onClick={addMask}>
                <Icon name="plus" />
              </Button>
            </div>
            {masks.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {masks.map((m) => (
                  <Badge key={m} variant="secondary" className="flex items-center gap-1">
                    {m}
                    <button type="button" onClick={() => removeMask(m)}>
                      <Icon name="x" className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </Field>
        );
      }}
    />
  );
}

/**
 * Em `filters`, relação se aplica a `type: 'select'` (alternativa às `options` estáticas).
 * Em `fields`, relação é `type: 'relation'` (N:1) ou `type: 'multi-relation'` (N:N — vira
 * checklist na tela de cadastro, salvando via rota aninhada `PUT /:resource/:id/:related`).
 * Em `columns`, só `type: 'multi-relation'` faz sentido (vira badges no grid).
 * Deixado em branco = sem relação (`options`/select comum), pra não conflitar com o estático.
 */
function RelationInputs({
  control,
  listName,
  index,
}: {
  control: Control<any>;
  listName: 'columns' | 'filters' | 'fields';
  index: number;
}) {
  const type = useWatch({ control, name: `layout.${listName}.${index}.type` });
  const showRelation =
    listName === 'filters'
      ? type === 'select'
      : type === 'relation' || type === 'multi-relation';

  if (!showRelation) return null;

  return (
    <>
      <Field className="col-span-6">
        <FieldLabel>Recurso relacionado (opcional)</FieldLabel>
        <Input
          {...control.register(`layout.${listName}.${index}.relation.resource`)}
          placeholder="ex: payment-methods"
        />
      </Field>
      <Field className="col-span-6">
        <FieldLabel>Campo exibido no select</FieldLabel>
        <Input
          {...control.register(`layout.${listName}.${index}.relation.displayField`)}
          placeholder="ex: description"
        />
      </Field>
    </>
  );
}

export type { ScreenLayout };
